import React, { useRef, useState } from 'react';
import { FileText, ImagePlus, Paperclip } from 'lucide-react';
import { usePasteImage } from '../../utils/usePasteImage';
import { toProductImageUrl } from '../../utils/imageUrl';

interface Props {
  label?: string;
  value: string;
  uploading: boolean;
  onFileSelected: (file: File | undefined) => void;
  enabled?: boolean;
  accept?: string;
}

const AttachmentDropzone: React.FC<Props> = ({
  label = 'Adjuntar comprobante (foto o PDF)',
  value,
  uploading,
  onFileSelected,
  enabled = true,
  accept = 'image/png,image/jpeg,image/webp,application/pdf',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  usePasteImage(onFileSelected, enabled);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFileSelected(file);
  };

  const isPdf = /\.pdf$/i.test(value);

  return (
    <div>
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`mt-1 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${
          isDragging ? 'border-primary-500 bg-primary-50' : 'border-gray-300 bg-gray-50'
        }`}
      >
        <ImagePlus className={`h-5 w-5 ${isDragging ? 'text-primary-500' : 'text-gray-400'}`} />
        <p className="text-sm text-gray-600">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="font-medium text-primary-600 hover:underline"
          >
            Hacé click para elegir un archivo
          </button>, arrastralo aquí o pegalo con Ctrl+V
        </p>
        <p className="text-xs text-gray-400">Foto o PDF · también funciona con una captura pegada desde WhatsApp Web</p>
        <input ref={inputRef} type="file" accept={accept} onChange={e => onFileSelected(e.target.files?.[0])} className="hidden" />
      </div>
      {uploading && <p className="mt-1 text-xs text-gray-500">Subiendo...</p>}
      {value && !uploading && (
        <div className="mt-2 flex items-center gap-2">
          {isPdf ? (
            <a href={toProductImageUrl(value)} target="_blank" rel="noreferrer" className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded border border-gray-200 bg-gray-50 text-gray-400 hover:border-primary-300">
              <FileText className="h-6 w-6" />
            </a>
          ) : (
            <a href={toProductImageUrl(value)} target="_blank" rel="noreferrer" className="block h-16 w-16 flex-shrink-0 overflow-hidden rounded border border-gray-200 hover:border-primary-300">
              <img src={toProductImageUrl(value)} alt="Vista previa del comprobante" className="h-full w-full object-cover" />
            </a>
          )}
          <a href={toProductImageUrl(value)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary-600">
            <Paperclip className="h-3 w-3" />Ver adjunto cargado
          </a>
        </div>
      )}
    </div>
  );
};

export default AttachmentDropzone;
